import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { set } from "date-fns";
import { Loader2 } from "lucide-react";

// --- Reusable HeaderPreview Component ---
export type HeaderPreviewProps = {
  title?: string;
  address?: string;
  phone?: string;
  logoUrl?: string;
  watermarkText?: string;
};

export const HeaderPreview: React.FC<HeaderPreviewProps> = ({
  title,
  address,
  phone,
  logoUrl,
  watermarkText = "A4 Page ",
}) => (
  <div
    style={{
      width: "100%",
      height: "100%",
      border: "1.5px solid #e0e0e0",
      boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
      padding: "32px",
      position: "relative",
      background: "#fff",
      borderRadius: "8px",
      overflow: "hidden",
    }}
    className="header-preview"
  >
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%) rotate(-30deg)",
        color: "rgba(0,0,0,0.07)",
        fontSize: "72px",
        fontWeight: 700,
        zIndex: 0,
        pointerEvents: "none",
        userSelect: "none",
        letterSpacing: "8px",
        textAlign: "center",
        width: "100%",
      }}
      className="watermark"
    >
      {watermarkText}
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "24px",
        zIndex: 1,
        position: "relative",
      }}
      className="header-content"
    >
      {logoUrl && (
        <img
          src={logoUrl}
          alt="Logo"
          style={{
            width: "80px",
            height: "80px",
            objectFit: "contain",
            marginRight: "24px",
          }}
          className="header-logo"
        />
      )}
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: "47px",
            fontWeight: 800,
            color: "#222",
            borderBottom: "3px solid black",
            lineHeight: "1.1",
            fontFamily: "Times New Roman",
          }}
          className="header-title"
        >
          {title || ""}
        </h1>
        <span
          style={{
            margin: 0,
            fontSize: "12px",
            fontWeight: 600,
            color: "#444",
            borderBottom: "1px solid black",
            paddingTop: "6px",
          }}
          className="header-address"
        >
          {address || ""}{" "}
        </span>
        <span
          style={{ fontWeight: 800, fontSize: "13px" }}
          className="header-phone"
        >
          {phone ? ` (Ph: ${phone})` : ""}
        </span>
      </div>
    </div>

    <style>{`
            @media (max-width: 768px) {
                .header-preview {
                    padding: 16px !important;
                    min-height: 200px;
                }
                
                .watermark {
                    font-size: 36px !important;
                    letter-spacing: 4px !important;
                }
                
                .header-content {
                    flex-direction: column !important;
                    text-align: center !important;
                }
                
                .header-logo {
                    width: 60px !important;
                    height: 60px !important;
                    margin-right: 0 !important;
                    margin-bottom: 12px !important;
                }
                
                .header-title {
                    font-size: 24px !important;
                    text-align: center;
                }
                
                .header-address {
                    font-size: 10px !important;
                    text-align: center;
                    display: block;
                }
                
                .header-phone {
                    font-size: 11px !important;
                    text-align: center;
                    display: block;
                    margin-top: 4px;
                }
            }
            
            @media (max-width: 480px) {
                .header-preview {
                    padding: 12px !important;
                    min-height: 180px;
                }
                
                .watermark {
                    font-size: 28px !important;
                    letter-spacing: 2px !important;
                }
                
                .header-title {
                    font-size: 20px !important;
                }
                
                .header-address {
                    font-size: 9px !important;
                }
                
                .header-phone {
                    font-size: 10px !important;
                }
            }
        `}</style>
  </div>
);

// --- Main Page Header Component ---
const BloodDonorPageHeader: React.FC = () => {
  const [title, setTitle] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [preview, setPreview] = useState({
    title: "",
    address: "",
    phone: "",
    logoUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState({
    title: "",
    address: "",
    phone: "",
    logo: "",
  });

  // Fetch header data function
  const fetchHeaderData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/page-header/getPageHeader`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        }
      );
      if (response.data.isSuccess && response.data.data) {
        const { title, address, phone, logo } = response.data.data;
        setTitle(title || "");
        setAddress(address || "");
        setPhone(phone || "");
        setPreview(prev => ({ ...prev, logoUrl: logo || "" }));
      } else {
        setTitle("");
        setAddress("");
        setPhone("");
        setPreview(prev => ({ ...prev, logoUrl: "" }));
      }
    } catch (error) {
      setTitle("");
      setAddress("");
      setPhone("");
      setPreview(prev => ({ ...prev, logoUrl: "" }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchHeaderData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setPreview({
      title,
      address,
      phone,
      logoUrl: logo ? URL.createObjectURL(logo) : preview.logoUrl,
    });
    // eslint-disable-next-line
  }, [title, address, phone, logo]);
  const validateForm = () => {
    const newErrors = {
      title: "",
      address: "",
      phone: "",
      logo: "",
    };

    let isValid = true;
    if (!title.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }
    if (!address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }
    if (!phone.trim()) {
      newErrors.phone = "Phone is required";
      isValid = false;
    }
    if (!logo) {
      newErrors.logo = "Logo is required";
      isValid = false;
    }

    setError(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const formData = new FormData();
    if (logo) {
      formData.append("file", logo);
    }
    formData.append("title", title);
    formData.append("address", address);
    formData.append("phone", phone);

    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_BLOOD_BANK}/page-header/setPageHeader`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Saved successfully");
      setLoading(false);
      await fetchHeaderData();
    } catch (error) {
      toast.error("Error saving data");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BLOOD_BANK}/page-header/removePageHeader`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setTitle("");
      setAddress("");
      setPhone("");
      setLogo(null);
      setPreview({ title: "", address: "", phone: "", logoUrl: "" });
      toast.success("Deleted successfully");
      await fetchHeaderData();
    } catch (error) {
      toast.error("Error deleting data");
    }
  };

  return (
    <>
      <div
        style={{
          height: "100%",
          background: "#f4f6fa",
        }}
        className="main-container"
      >
        {/* --- Top Bar/Header --- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "56px",
            background: "#fff",
            borderBottom: "1px solid #e0e0e0",
            padding: "0 32px",
            marginBottom: "24px",
          }}
          className="top-bar"
        >
          <h2
            style={{
              margin: 0,
              fontWeight: 600,
              fontSize: "22px",
              letterSpacing: "1px",
            }}
            className="page-title"
          >
            Page Header
          </h2>
        </div>

        {/* --- Main Content --- */}
        <div
          style={{ display: "flex", gap: "32px", padding: "0 32px" }}
          className="main-content"
        >
          {/* --- Left: Form Section --- */}
          <div
            style={{
              flex: 1,
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              padding: "32px",
              minWidth: "340px",
              maxWidth: "420px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
            className="form-section"
          >
            <div style={{ marginBottom: "18px" }} className="form-group">
              <label
                style={{
                  fontWeight: 500,
                  color: "#333",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Title
              </label>
              <input
                // required
                className="search-field form-input"
                type="text"
                placeholder="Enter title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                }}
              />
              <span style={{ color: "red", fontSize: "10px" }}>
                {error.title}
              </span>
            </div>
            <div style={{ marginBottom: "18px" }} className="form-group">
              <label
                style={{
                  fontWeight: 500,
                  color: "#333",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Address
              </label>
              <input
                required
                className="search-field form-input"
                type="text"
                placeholder="Enter address"
                value={address}
                onChange={e => setAddress(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                }}
              />
              <span style={{ color: "red", fontSize: "10px" }}>
                {error.address}
              </span>
            </div>
            <div style={{ marginBottom: "18px" }} className="form-group">
              <label
                style={{
                  fontWeight: 500,
                  color: "#333",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Phone Number
              </label>
              <input
                required
                className="search-field form-input"
                type="text"
                placeholder="Enter phone number"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                }}
              />
              <span style={{ color: "red", fontSize: "10px" }}>
                {error.phone}
              </span>
            </div>
            <div style={{ marginBottom: "24px" }} className="form-group">
              <label
                style={{
                  fontWeight: 500,
                  color: "#333",
                  marginBottom: "6px",
                  display: "block",
                }}
              >
                Logo
              </label>
              <input
                required
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={e => {
                  if (e.target.files && e.target.files[0])
                    setLogo(e.target.files[0]);
                }}
                style={{ display: "none" }}
              />
              <label
                htmlFor="logo-upload"
                style={{
                  display: "inline-block",
                  padding: "8px 18px",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: 500,
                  border: "none",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  fontSize: "15px",
                }}
                className="upload-button"
              >
                {logo ? "Change Logo" : "Upload Logo"}
              </label>
              {logo && (
                <span
                  style={{
                    marginLeft: "12px",
                    color: "#555",
                    fontSize: "14px",
                    verticalAlign: "middle",
                  }}
                  className="file-name"
                >
                  {logo.name}
                </span>
              )}
            </div>
            <span style={{ color: "red", fontSize: "10px" }}>{error.logo}</span>

            {/* --- Action Buttons --- */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "16px",
              }}
              className="action-buttons"
            >
              <button
                onClick={handleSave}
                disabled={loading}
                className="save-button"
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: loading ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  fontSize: "16px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: loading ? 0.6 : 1,
                  transform: loading ? "scale(0.98)" : "scale(1)",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
              <button
                onClick={() => setShowConfirm(true)}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: 500,
                  fontSize: "16px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  transition: "background 0.2s",
                }}
                className="delete-button"
              >
                Delete
              </button>
            </div>
          </div>

          {/* --- Right: Preview Section --- */}
          <div
            style={{
              flex: 2,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
            className="preview-section"
          >
            <HeaderPreview
              title={preview.title}
              address={preview.address}
              phone={preview.phone}
              logoUrl={preview.logoUrl}
            />
          </div>

          {showConfirm && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
              className="confirm-dialog"
            >
              <div
                style={{
                  background: "#fff",
                  padding: "32px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                  minWidth: "320px",
                  textAlign: "center",
                }}
                className="confirm-content"
              >
                <h3 style={{ marginBottom: "18px" }}>
                  Are you sure you want to delete?
                </h3>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "18px",
                  }}
                  className="confirm-buttons"
                >
                  <button
                    onClick={async () => {
                      setShowConfirm(false);
                      await handleDelete();
                    }}
                    style={{
                      padding: "8px 24px",
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: "15px",
                    }}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setShowConfirm(false)}
                    style={{
                      padding: "8px 24px",
                      backgroundColor: "#e0e0e0",
                      color: "#222",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: "15px",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>

      {/* Mobile Responsive Styles */}
      <style>{`
                @media (max-width: 768px) {
                    .top-bar {
                        padding: 0 16px !important;
                        height: 48px !important;
                    }
                    
                    .page-title {
                        font-size: 18px !important;
                    }
                    
                    .main-content {
                        flex-direction: column !important;
                        gap: 20px !important;
                        padding: 0 16px !important;
                    }
                    
                    .form-section {
                        min-width: unset !important;
                        max-width: unset !important;
                        padding: 20px !important;
                        margin-bottom: 20px;
                    }
                    
                    .preview-section {
                        flex: unset !important;
                        width: 100%;
                        min-height: 250px;
                    }
                    
                    .action-buttons {
                        justify-content: center !important;
                        flex-wrap: wrap;
                    }
                    
                    .save-button, .delete-button {
                        min-width: 100px;
                        font-size: 14px !important;
                        padding: 8px 16px !important;
                    }
                    
                    .file-name {
                        display: block !important;
                        margin-left: 0 !important;
                        margin-top: 8px !important;
                        font-size: 12px !important;
                    }
                    
                    .upload-button {
                        font-size: 14px !important;
                        padding: 6px 14px !important;
                    }
                    
                    .confirm-content {
                        margin: 0 16px;
                        padding: 24px !important;
                        min-width: 280px !important;
                    }
                    
                    .confirm-buttons {
                        flex-wrap: wrap;
                        gap: 12px !important;
                    }
                    
                    .confirm-buttons button {
                        min-width: 80px;
                        font-size: 14px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .top-bar {
                        padding: 0 12px !important;
                    }
                    
                    .main-content {
                        padding: 0 12px !important;
                    }
                    
                    .form-section {
                        padding: 16px !important;
                    }
                    
                    .form-group {
                        margin-bottom: 16px !important;
                    }
                    
                    .form-input {
                        padding: 8px 10px !important;
                        font-size: 14px !important;
                    }
                    
                    .action-buttons {
                        margin-top: 12px !important;
                    }
                    
                    .preview-section {
                        min-height: 200px;
                    }
                    
                    .confirm-content {
                        padding: 20px !important;
                        min-width: 250px !important;
                    }
                    
                    .confirm-content h3 {
                        font-size: 16px !important;
                        margin-bottom: 16px !important;
                    }
                }
            `}</style>
    </>
  );
};

export default BloodDonorPageHeader;
